import { FileText, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CollapsibleCard } from "../CollapsibleCard";
import {
  packagingTypesMap,
  packagingMaterialsMap,
  plasticTypesMap,
} from "../../constants";
import { renderBadgeList } from "../../utils";

interface CommodityDetailsSectionProps {
  submission: any;
  defaultOpen?: boolean;
  canCreateSubmission?: boolean;
  projectDetails?: any;
  navigate?: (path: string) => void;
}

export const CommodityDetailsSection = ({
  submission,
  defaultOpen = false,
  canCreateSubmission,
  projectDetails,
  navigate,
}: CommodityDetailsSectionProps) => {
  return (
    <CollapsibleCard title="3. Commodity Details" defaultOpen={defaultOpen}>
      <div className="space-y-4">
        {submission.commodities?.length > 0 ? (
          <div className="space-y-4">
            {submission.commodities.map((commodity: any, index: number) => {
              const hasPlastic =
                commodity.packagingMaterials?.includes("plastic");
              return (
                <div key={index} className="border-b pb-4 last:border-0">
                  <h3 className="font-medium mb-2">Commodity #{index + 1}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <div className="flex justify-between md:flex-col md:justify-start md:space-y-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        Product
                      </span>
                      <span className="text-sm font-medium">
                        {commodity.productName}
                      </span>
                    </div>
                    <div className="flex justify-between md:flex-col md:justify-start md:space-y-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        Packaging Type
                      </span>
                      <span className="text-sm font-medium">
                        {packagingTypesMap[commodity.packagingType] ||
                          commodity.packagingType}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1 md:space-y-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        Materials
                      </span>
                      {renderBadgeList(
                        commodity.packagingMaterials,
                        packagingMaterialsMap
                      )}
                    </div>
                    <div className="flex justify-between md:flex-col md:justify-start md:space-y-1">
                      <span className="text-sm font-medium text-muted-foreground">
                        Fully Encapsulated
                      </span>
                      <span className="text-sm font-medium">
                        {commodity.isFullyEncapsulated ? "Yes" : "No"}
                      </span>
                    </div>
                    {hasPlastic && commodity.plasticTypes && (
                      <div className="flex flex-col gap-1 md:space-y-1 md:col-span-2">
                        <span className="text-sm font-medium text-muted-foreground">
                          Plastic Types
                        </span>
                        {renderBadgeList(
                          commodity.plasticTypes,
                          plasticTypesMap
                        )}
                      </div>
                    )}
                    {commodity.canBeReconfigured !== undefined && (
                      <div className="flex justify-between md:flex-col md:justify-start md:space-y-1">
                        <span className="text-sm font-medium text-muted-foreground">
                          Can Be Reconfigured
                        </span>
                        <span className="text-sm font-medium">
                          {commodity.canBeReconfigured ? "Yes" : "No"}
                        </span>
                      </div>
                    )}
                    {commodity.canBeReconfigured &&
                      commodity.reconfigurationNotes && (
                        <div className="flex flex-col gap-1 md:space-y-1 md:col-span-2">
                          <span className="text-sm font-medium text-muted-foreground">
                            Reconfiguration Notes
                          </span>
                          <span className="text-sm font-medium whitespace-pre-line">
                            {commodity.reconfigurationNotes}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-4 md:py-12">
            {!submission.files?.commodityLetter && (
              <>
                <FileText className="h-12 w-12 text-muted-foreground/50 mb-4 hidden md:block" />
                <h3 className="text-lg font-medium mb-2 hidden md:block">
                  No commodities added
                </h3>
                <p className="text-muted-foreground mb-4">
                  {submission.files?.commodityLetter
                    ? ""
                    : "No commodities added"}
                </p>
              </>
            )}
            {canCreateSubmission && navigate && projectDetails && (
              <Button
                onClick={() =>
                  navigate(`/projects/${projectDetails?._id}/submissions/new`)
                }
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Commodity
              </Button>
            )}
          </div>
        )}

        <div className="space-y-3 pt-4 border-t mt-4">
          <div className="flex justify-between md:flex-col md:justify-start md:space-y-1">
            <span className="text-sm font-medium text-muted-foreground">
              Hazardous Materials
            </span>
            <span className="text-sm font-medium">
              {submission.hasHazardousMaterials ? "Yes" : "No"}
            </span>
          </div>
          {submission.hasHazardousMaterials &&
            submission.hazardousMaterialsDetails && (
              <div className="flex flex-col gap-1 md:space-y-1 md:mt-2">
                <span className="text-sm font-medium text-muted-foreground">
                  Details
                </span>
                <span className="text-sm font-medium">
                  {submission.hazardousMaterialsDetails}
                </span>
              </div>
            )}
        </div>

        {submission.files?.commodityLetter && (
          <div className="mt-4">
            <a
              href={submission.files.commodityLetter.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm font-medium text-primary hover:underline"
            >
              <FileText className="mr-2 h-4 w-4" /> Commodity Letter
            </a>
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
};
